import { useRouter, usePathname } from "next/navigation";
import { useOnboardingStore, onboardingProps } from "@/store/onboardingStore";
import {
  getOnboardingStepFallbackPath,
  getOnboardingStepRoute,
  getOnboardingStepUi,
  getStepNameFromOnboardingPath,
  isOnboardingPath,
} from "@jurni/schemas";
import { useCallback, useMemo } from "react";

export const useOnboardingSteps = () => {
  const router = useRouter();
  const pathname = usePathname();
  const onboardingStepsPaths = useOnboardingStore(
    onboardingProps.onboardingStepsPaths,
  );
  const stepsCount = useOnboardingStore(onboardingProps.stepsCount);
  const currentRouteStepName = getStepNameFromOnboardingPath(pathname);
  const currentStepUi = getOnboardingStepUi(currentRouteStepName);

  const currentStepIndex = useMemo(
    () => onboardingStepsPaths.findIndex((path) => path === pathname),
    [onboardingStepsPaths, pathname],
  );
  const isOnboardingAuthorizedPath = currentStepIndex !== -1;
  const fallbackPreviousPath =
    getOnboardingStepFallbackPath(currentRouteStepName);
  const canUseBackNavigation = currentStepUi.showBack;
  const shouldShowStepper =
    isOnboardingAuthorizedPath && currentStepUi.showStepper;

  const goNext = useCallback(() => {
    const paths = useOnboardingStore.getState().onboardingStepsPaths;
    const currentPathname =
      typeof window !== "undefined" ? window.location.pathname : "";

    const stepIndex = paths.findIndex((path) => path === currentPathname);
    if (stepIndex === -1) return;

    const nextIndex = stepIndex + 1;
    if (nextIndex < paths.length) {
      router.replace(paths[nextIndex]);
    }
  }, [router]);

  const goBack = useCallback(() => {
    const paths = useOnboardingStore.getState().onboardingStepsPaths;
    const currentPathname =
      typeof window !== "undefined" ? window.location.pathname : "";
    const currentStepName = getStepNameFromOnboardingPath(currentPathname);
    if (!getOnboardingStepUi(currentStepName).showBack) {
      return;
    }

    const stepIndex = paths.findIndex((path) => path === currentPathname);
    if (stepIndex === -1) {
      const previousPath = getOnboardingStepFallbackPath(currentStepName);
      if (previousPath) {
        router.replace(previousPath);
        return;
      }
      router.back();
      return;
    }

    const previousIndex = stepIndex - 1;
    if (previousIndex >= 0) {
      router.replace(paths[previousIndex]);
    } else {
      router.back();
    }
  }, [router]);

  return {
    goNext,
    goBack,
    isOnboardingAuthorizedPath,
    shouldShowStepper,
    currentStepIndex,
    stepName: getOnboardingStepRoute(currentRouteStepName)?.name ?? "",
    stepsCount,
    canGoBack:
      canUseBackNavigation &&
      (currentStepIndex > 0 ||
        !!fallbackPreviousPath ||
        !isOnboardingPath(pathname)),
    canGoNext:
      currentStepIndex !== -1 &&
      currentStepIndex < onboardingStepsPaths.length - 1,
  };
};
